/* eslint-disable no-debugger, no-console */
import { splitIndexKey, getCategoryDisplayName, getPolarityDisplayName, getEntityDisplayName } from "@/commons/util.js";
import _orderby from "lodash.orderby";

export const ApexBaseChartOptions = {
    height: 210,
    colors: ["#18B7BE", "#306080", "#dd723d", "#7888ae",],
    chart: {
        zoom: {
            enabled: false
        },
        toolbar: {
            show: false
        },
        animations: {
            enabled: false
        },
        markers: {
            size: 0
        },
        dataLabels: {
            enabled: false
        },
    },
    legend: {
        show: false
    },
};


export const GmapsOptions = {
    defaultLocation: {
        lat: 4.5980985,
        lng: -74.0753303
    },
    mapOptions: {
        gestureHandling: "greedy",
        controlSize: 22
    },
};

export const getPolarityColor = (polarity, polarityColorMap) => {
    let polarityNorm = polarity.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (polarityNorm in polarityColorMap) {
        return polarityColorMap[polarityNorm];
    }
    return polarityColorMap.default || "#7272DD";
};

export const getCategoryColor = (category, categoryColorMap) => {
    let categoryNorm = category.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (categoryNorm in categoryColorMap) {
        return categoryColorMap[categoryNorm];
    }
    return categoryColorMap.default || "#178CA4";
};

export const DEFAULT_CHART_DATA_SOURCES = {
    results: data => data.results,
    wordcloud1: data => data.aggregations.words_sign.buckets,
    wordcloud2: data => data.aggregations.words_freq.buckets,
    categories: data => data.aggregations.sent.filter_sent.categories.buckets,
    entities: data => data.aggregations.sent.filter_sent.entities.buckets,
    polarities: data => data.aggregations.sent.filter_sent.polarity.buckets,
    authors: data => data.aggregations.authors.buckets,
    sources: data => data.aggregations.sources.buckets,

    authors_categories: data => data.aggregations.authors_category.buckets,
    categoriesActivityNews: data => data.aggregations.categories_activity.buckets,
    cities_categories: data => data.aggregations.cities_category.buckets,
    cities_activity: data => data.aggregations.cities_activity.buckets,
    states_categories: data => data.aggregations.states_category.buckets,
    states_activity: data => data.aggregations.states_activity.buckets,
    categories_polarity: data => data.aggregations.sent.filter_sent.categories.buckets,
    entities_polarity: data => data.aggregations.sent.filter_sent.entities.buckets,
    entities_categories: data => data.aggregations.sent.filter_sent.entities.buckets,

    authors_activity: data => data.aggregations.authors_activity.buckets,
    category_activity: data => data.aggregations.sent.filter_sent.categories_activity.buckets,
    entities_activity: data => data.aggregations.sent.filter_sent.entities_activity.buckets,
    polarity_activity: data => data.aggregations.sent.filter_sent.polarity_activity.buckets,
    category_topic: data => data.aggregations.sent.filter_sent.categories.buckets,

    entities_link: data => data.results,
    googleMapMarkers: data => data.results,
    googleMapHeat: data => data.results,
    googleMapBubbles: data => data.results,
    vectorMapWorld: data => data.aggregations.countries.buckets,
    vectorMapColombia: data => data.aggregations.states.buckets,
};

export const getChartOptions = ({
    categoryColorMap = {
        "economia": "#127A79",
        "seguridad": "#903250",
        "derechos humanos": "#54D2D2",
        "politica": "#133C65",
        "social": "#7AB8A3",
        "gestion del riesgo de desastre": "#7C6A6A",
        "salud": "#6F7AB7",
        "proceso de paz": "#BBD4CE",
        "movilidad": "#679186",
        "infraestructura": "#FFCB00",
        "ambiental": "#8DD55F",
        "deporte": "#072448",
        "judicial": "#EDAE49",
        "diversion y entretenimiento": "#D9FFFF",
        "educacion": "#F9B4AB",
        "religion": "#D9E3FF",
        "arte y cultura": "#FFF780",
        "militar": "#004999",
        "ciencia y tecnologia": "#B20092",
        "medios de comunicacion": "#98A5B3",
        "default": "#178CA4"
    },
    max_categories = 6,
    polarityColorMap = {
        "positivo": "#34bf49",
        "negativo": "#ff4c4c",
        "ironia": "#7888ae",
        "neutral": "#b9c0c5",
        "default": "#7272DD"
    },
    entitiesTypeMap = {
        "PER": {
            color: "#627fb1",
            faIconName: "fa-user",
            faIconCode: "\uf007"
        },
        "ORG": {
            color: "#bf3d3d",
            faIconName: "fa-building",
            faIconCode: "\uf1ad"
        },
        "LOC": {
            color: "#62a567",
            faIconName: "fa-globe",
            faIconCode: "\uf0ac"
        },
        "MENTION": {
            color: "#1DA1F2",
            faIconName: "fa-twitter",
            faIconCode: "\uf099"
        },
        "HASHTAG": {
            color: "#d0cd79",
            faIconName: "fa-hashtag",
            faIconCode: "\uf292"
        },
        "URL": {
            color: "#d3d3d3",
            faIconName: "fa-link",
            faIconCode: "\uf0c1"
        }
    },
    max_words = 20,
    chartNames = {},
    dataSources = DEFAULT_CHART_DATA_SOURCES }) => {

    return {
        results: data => {
            const resultsData = dataSources.results(data);
            return {
                title: chartNames.results || "Resultados",
                type: "results",
                options: {
                    results: resultsData
                }
            };
        },
        wordcloud: data => {
            const chartData1 = dataSources.wordcloud1(data);
            const chartData2 = dataSources.wordcloud2(data);
            let words = [];
            if (chartData1.length > 0) {
                words = chartData1
                    .map((b) => [b.key, b.score])
                    .splice(0, max_words);
            } else if (chartData2.length > 0) {
                words = chartData2
                    .map((b) => [b.key, b.score])
                    .splice(0, max_words);
            }
            return {
                title: chartNames.wordcloud || "Nube de Palabras",
                type: "word-cloud",
                options: { words }
            };
        },
        categories: data => {
            const chartData = dataSources.categories(data);
            let series = [
                {
                    name: "Categorias",
                    data: chartData.map(b => {
                        return {
                            x: getCategoryDisplayName(b.key),
                            y: b.doc_count,
                            fillColor: getCategoryColor(b.key, categoryColorMap),
                            key: b.key
                        };
                    }).splice(0, max_categories),
                }
            ];

            return {
                title: chartNames.categories || "Categorias",
                type: "bar-chart",
                options: {
                    plotOptions: {
                        plotOptions: {
                            bar: {
                                columnWidth: "50%",
                                horizontal: true,
                            },
                        },
                        xaxis: {
                            tickPlacement: "on",
                        },
                    },
                    series: series,
                }
            };
        },
        entities: data => {
            const chartData = dataSources.entities(data);
            let entities = chartData.map(b => {
                return getEntityDisplayName(b.key);
            }).splice(0, max_categories);
            let series = [
                {
                    name: "Entidades",
                    data: chartData.map(b => {
                        return b.doc_count;
                    }).splice(0, max_categories)
                }
            ];
            return {
                title: chartNames.entities || "Entidades",
                type: "bar-chart",
                options: {
                    plotOptions: {
                        plotOptions: {
                            bar: {
                                columnWidth: "50%",
                                horizontal: true,
                            },
                        },
                        xaxis: {
                            entities,
                            tickPlacement: "on",
                        },
                    },
                    series: series
                },
            };
        },
        polarities: data => {
            const chartData = dataSources.polarities(data);
            let polarityDict = chartData.reduce((obj, b) => {
                obj[b.key.trim().toLowerCase()] = b.doc_count;
                return obj;
            }, {});
            let series = new Array(4).fill(0);
            series[0] = polarityDict["positivo"] || 0;
            series[1] = polarityDict["negativo"] || 0;
            series[2] = polarityDict["ironia"] || 0;
            series[3] = polarityDict["neutral"] || 0;
            const chartLabels = ["Positivo", "Negativo", "Ironía", "Neutral"];
            return {
                title: chartNames.polarity || "Polaridad",
                type: "pie-chart",
                options: {
                    plotOptions: {
                        labels: chartLabels,
                        colors: chartLabels.map(l => getPolarityColor(l, polarityColorMap)),
                        fill: {
                            opacity: 0.88,
                        },
                    },
                    series: series
                },
            };
        },
        authors: data => {
            const chartData = dataSources.authors(data);
            let categories = chartData.map(b => {
                return b.key;
            }).splice(0, max_categories);
            let series = [
                {
                    name: "Autores",
                    data: chartData.map(b => {
                        return b.doc_count;
                    }).splice(0, max_categories)
                }
            ];
            return {
                title: chartNames.authors || "Autores",
                type: "bar-chart",
                options: {
                    plotOptions: {
                        plotOptions: {
                            bar: {
                                columnWidth: "50%",
                                horizontal: true,
                            },
                        },
                        xaxis: {
                            categories,
                            tickPlacement: "on",
                        },
                    },
                    series: series
                },
            };
        },
        sources: data => {
            const chartData = dataSources.sources(data);
            let categories = chartData.map(b => {
                return b.key;
            }).splice(0, max_categories);
            let series = [
                {
                    name: "Fuentes",
                    data: chartData.map(b => {
                        return b.doc_count;
                    }).splice(0, max_categories)
                }
            ];
            return {
                title: chartNames.sources || "Fuentes",
                type: "bar-chart",
                options: {
                    plotOptions: {
                        plotOptions: {
                            bar: {
                                horizontal: true,
                            },
                        },
                        xaxis: { categories },
                    },
                    series: series
                },
            };
        },
        authors_categories: data => {
            const chartData = dataSources.authors_categories(data);
            let authors = chartData.map(authorBucket => {
                return authorBucket.key
            });
            let authorsToIndex = authors.reduce((obj, author, index) => {
                obj[author] = index;
                return obj;
            }, {});
            let seriesDict = {};
            chartData.forEach(authorBucket => {
                authorBucket.entry_count.entry_count.buckets.forEach(categoryBucket => {
                    let authorIndex = authorsToIndex[authorBucket.key];
                    let categoryNorm = categoryBucket.key.trim().toLowerCase();
                    if (!Object.prototype.hasOwnProperty.call(seriesDict, categoryNorm)) {
                        seriesDict[categoryNorm] = new Array(authors.length).fill(0);
                    }
                    seriesDict[categoryNorm][authorIndex] = categoryBucket.doc_count;
                });
            });
            let series = Object.entries(seriesDict).map(([category, data]) => {
                return {
                    name: getCategoryDisplayName(category),
                    data: data
                };
            });
            return {
                title: chartNames.authorsVsCategory || "Autores vs Categoría",
                type: "stacked-bar-chart",
                options: {
                    plotOptions: {
                        bar: {
                            distributed: false
                        },
                        xaxis: {
                            categories: authors
                        },
                    },
                    series: series
                },
            };
        },
        cities_categories: data => {
            const chartData = dataSources.cities_categories(data);
            let cities = chartData.map(cityBucket => {
                return cityBucket.key
            });
            let citiesToIndex = cities.reduce((obj, city, index) => {
                obj[city] = index;
                return obj;
            }, {});
            let seriesDict = {};
            chartData.forEach(cityBucket => {
                cityBucket.entry_count.entry_count.buckets.forEach(categoryBucket => {
                    let cityIndex = citiesToIndex[cityBucket.key];
                    let categoryNorm = categoryBucket.key.trim().toLowerCase();
                    if (!Object.prototype.hasOwnProperty.call(seriesDict, categoryNorm)) {
                        seriesDict[categoryNorm] = new Array(cities.length).fill(0);
                    }
                    seriesDict[categoryNorm][cityIndex] = categoryBucket.doc_count;
                });
            });
            let series = Object.entries(seriesDict).map(([category, data]) => {
                return {
                    name: getCategoryDisplayName(category),
                    color: getCategoryColor(category, categoryColorMap),
                    data: data
                };
            });
            return {
                title: chartNames.cityVsCategory || "Ciudad vs Categoría",
                type: "stacked-bar-chart",
                options: {
                    plotOptions: {
                        colors: series.map(entry => entry.color),
                        fill: {
                            opacity: 0.88,
                        },
                        bar: {
                            distributed: false
                        },
                        xaxis: {
                            categories: cities
                        },
                    },
                    series: series
                },
            };
        },
        cities_activity: data => {
            const chartData = dataSources.cities_activity(data);
            let series = chartData.map(cityBucket => {
                let data = cityBucket.entry_count.buckets.map(b => {
                    return {
                        x: b.key,
                        y: b.doc_count
                    }
                });
                return {
                    name: cityBucket.key,
                    data
                };
            });
            return {
                title: chartNames.citiesActivity || "Actividad Ciudades",
                type: "line-chart",
                options: {
                    plotOptions: {
                        xaxis: {
                            type: "datetime"
                        },
                        legend: {
                            show: true,
                            position: "bottom"
                        },
                    },
                    series: series
                },
            };
        },
        states_categories: data => {
            const chartData = dataSources.states_categories(data);
            let states = chartData.map(stateBucket => {
                return stateBucket.key
            });
            let stateToIndex = states.reduce((obj, state, index) => {
                obj[state] = index;
                return obj;
            }, {});
            let seriesDict = {};
            chartData.forEach(stateBucket => {
                stateBucket.entry_count.entry_count.buckets.forEach(categoryBucket => {
                    let stateIndex = stateToIndex[stateBucket.key];
                    let categoryNorm = categoryBucket.key.trim().toLowerCase();
                    if (!Object.prototype.hasOwnProperty.call(seriesDict, categoryNorm)) {
                        seriesDict[categoryNorm] = new Array(states.length).fill(0);
                    }
                    seriesDict[categoryNorm][stateIndex] = categoryBucket.doc_count;
                });
            });
            let series = Object.entries(seriesDict).map(([category, data]) => {
                return {
                    name: getCategoryDisplayName(category),
                    color: getCategoryColor(category, categoryColorMap),
                    data: data
                };
            });
            return {
                title: chartNames.stateVsCategory || "Departamento vs Categoría",
                type: "stacked-bar-chart",
                options: {
                    plotOptions: {
                        colors: series.map(entry => entry.color),
                        fill: {
                            opacity: 0.88,
                        },
                        bar: {
                            distributed: false
                        },
                        xaxis: {
                            categories: states
                        },
                    },
                    series: series
                },
            };
        },
        states_activity: data => {
            const chartData = dataSources.states_activity(data);
            let series = chartData.map(stateBucket => {
                let data = stateBucket.entry_count.buckets.map(b => {
                    return {
                        x: b.key,
                        y: b.doc_count
                    }
                });
                return {
                    name: stateBucket.key,
                    data
                };
            });
            return {
                title: chartNames.statesActivity || "Actividad Departamentos",
                type: "line-chart",
                options: {
                    plotOptions: {
                        xaxis: {
                            type: "datetime"
                        },
                        legend: {
                            show: true,
                            position: "bottom"
                        },
                    },
                    series: series
                },
            };
        },
        categories_polarity: data => {
            const chartData = dataSources.categories_polarity(data);
            let categories = chartData.map(categoryBucket => {
                return categoryBucket.key
            });
            let categoryToIndex = categories.reduce((obj, category, index) => {
                obj[category] = index;
                return obj;
            }, {});
            let seriesDict = {};
            chartData.forEach(categoryBucket => {
                categoryBucket.categories_polarity.buckets.forEach(polarityBucket => {
                    let categoryIndex = categoryToIndex[categoryBucket.key];
                    let polarityNorm = polarityBucket.key.trim().toLowerCase();
                    if (!Object.prototype.hasOwnProperty.call(seriesDict, polarityNorm)) {
                        seriesDict[polarityNorm] = new Array(categories.length).fill(0);
                    }
                    seriesDict[polarityNorm][categoryIndex] = polarityBucket.doc_count;
                });
            });
            categories = categories.map(c => getCategoryDisplayName(c)).splice(0, max_categories);
            let series = Object.entries(seriesDict).map(([polarity, data]) => {
                let polarityDisplay = getPolarityDisplayName(polarity)
                return {
                    name: polarityDisplay,
                    color: getPolarityColor(polarity, polarityColorMap),
                    data: data.splice(0, max_categories)
                };
            });

            return {
                title: chartNames.categoriesVsPolarity || "Categoría vs Polaridad",
                type: "stacked-bar-chart",
                options: {
                    plotOptions: {
                        colors: series.map(entry => entry.color),
                        fill: {
                            opacity: 0.88,
                        },
                        bar: {
                            distributed: false
                        },
                        xaxis: {
                            categories
                        },
                    },
                    series: series
                },
            };
        },
        entities_polarity: data => {
            const chartData = dataSources.entities_polarity(data);
            let entities = chartData.map(entityBucket => {
                return entityBucket.key
            });
            let entityToIndex = entities.reduce((obj, category, index) => {
                obj[category] = index;
                return obj;
            }, {});
            let seriesDict = {};
            chartData.forEach(entityBucket => {
                entityBucket.entities_polarity.buckets.forEach(polarityBucket => {
                    const entityIndex = entityToIndex[entityBucket.key];
                    const polarityNorm = polarityBucket.key.trim().toLowerCase();
                    if (!Object.prototype.hasOwnProperty.call(seriesDict, polarityNorm)) {
                        seriesDict[polarityNorm] = new Array(entities.length).fill(0);
                    }
                    seriesDict[polarityNorm][entityIndex] = polarityBucket.doc_count;
                });
            });
            entities = entities.map(c => getEntityDisplayName(c)).splice(0, max_categories);
            let series = Object.entries(seriesDict).map(([polarity, data]) => {
                const polarityDisplay = getPolarityDisplayName(polarity)
                return {
                    name: polarityDisplay,
                    color: getPolarityColor(polarity, polarityColorMap),
                    data: data.splice(0, max_categories)
                };
            });
            return {
                title: chartNames.entitiesVsPolarity || "Entidades vs Polaridad",
                type: "stacked-bar-chart",
                options: {
                    plotOptions: {
                        colors: series.map(entry => entry.color),
                        fill: {
                            opacity: 0.88,
                        },
                        bar: {
                            distributed: false
                        },
                        xaxis: {
                            categories: entities
                        },
                    },
                    series: series
                },
            };
        },
        entities_categories: data => {
            const chartData = dataSources.entities_categories(data);
            let entities = chartData.map(entityBucket => {
                return entityBucket.key
            });
            let entityToIndex = entities.reduce((obj, category, index) => {
                obj[category] = index;
                return obj;
            }, {});
            let seriesDict = {};
            chartData.forEach(entityBucket => {
                entityBucket.entities_categories.buckets.forEach(categoryBucket => {
                    let entityIndex = entityToIndex[entityBucket.key];
                    let categoryNorm = categoryBucket.key.trim().toLowerCase();
                    if (!Object.prototype.hasOwnProperty.call(seriesDict, categoryNorm)) {
                        seriesDict[categoryNorm] = new Array(entities.length).fill(0);
                    }
                    seriesDict[categoryNorm][entityIndex] = categoryBucket.doc_count;
                });
            })
            entities = entities.map(c => getEntityDisplayName(c)).splice(0, max_categories);
            let series = Object.entries(seriesDict).map(([category, data]) => {
                return {
                    name: getCategoryDisplayName(category),
                    color: getCategoryColor(category, categoryColorMap),
                    data: data.splice(0, max_categories),
                };
            }).splice(0, max_categories);
            return {
                title: chartNames.entitiesVsCategories || "Entidades vs Categoria",
                type: "stacked-bar-chart",
                options: {
                    plotOptions: {
                        colors: series.map(entry => entry.color),
                        fill: {
                            opacity: 0.88,
                        },
                        bar: {
                            distributed: false
                        },
                        xaxis: {
                            categories: entities
                        },
                    },
                    series: series
                },
            };
        },
        authors_activity: data => {
            const chartData = dataSources.authors_activity(data);
            let series = chartData.map(authorBucket => {
                let point = authorBucket.entry_count.buckets.map(b => {
                    return {
                        x: b.key,
                        y: b.doc_count
                    }
                });
                return {
                    name: authorBucket.key,
                    data: point
                };
            });
            return {
                title: chartNames.authorsActivity || "Actividad Autores",
                type: "line-chart",
                options: {
                    plotOptions: {
                        xaxis: {
                            type: "datetime"
                        },
                        legend: {
                            show: true,
                            position: "bottom"
                        },
                    },
                    series: series
                },
            };
        },
        category_activity: data => {
            const chartData = dataSources.category_activity(data);
            let series = chartData.splice(0, max_categories).map(categoryBucket => {
                let point = categoryBucket.reverse.entry_count.buckets.filter(b => b.doc_count > 0).map(b => {
                    return {
                        x: b.key,
                        y: b.doc_count
                    };
                });
                return {
                    name: getCategoryDisplayName(categoryBucket.key),
                    color: getCategoryColor(categoryBucket.key, categoryColorMap),
                    data: point,

                };
            });
            return {
                title: chartNames.categoriesActivity || "Actividad Categorias",
                type: "line-chart",
                options: {
                    plotOptions: {
                        xaxis: {
                            type: "datetime"
                        },
                        legend: {
                            show: true,
                            position: "bottom"
                        },
                    },
                    series: series
                },
            };
        },
        categoriesActivityNews: data => {
            const chartData = dataSources.categoriesActivityNews(data);
            let series = chartData.map(categoryBucket => {
                let point = categoryBucket.entry_count.buckets.map(b => {
                    return {
                        x: b.key,
                        y: b.doc_count
                    }
                });
                return {
                    name: getCategoryDisplayName(categoryBucket.key),
                    data: point
                };
            });
            return {
                title: chartNames.categoriesActivityNews || "Actividad Categorias",
                type: "line-chart",
                options: {
                    plotOptions: {
                        xaxis: {
                            type: "datetime"
                        },
                        legend: {
                            show: true,
                            position: "bottom"
                        },
                    },
                    series: series
                },
            };
        },
        entities_activity: data => {
            const chartData = dataSources.entities_activity(data);
            let series = chartData.splice(0, max_categories).map(entityBucket => {
                let data = entityBucket.reverse.entry_count.buckets.filter(b => b.doc_count > 0).map(b => {
                    return {
                        x: b.key,
                        y: b.doc_count
                    };
                });
                return {
                    name: getEntityDisplayName(entityBucket.key),
                    data
                };
            });
            return {
                title: "Actividad Entidades",
                type: "line-chart",
                options: {
                    plotOptions: {
                        xaxis: {
                            type: "datetime"
                        },
                        legend: {
                            show: true,
                            position: "bottom"
                        },
                    },
                    series: series
                },
            };
        },
        polarity_activity: data => {
            const chartData = dataSources.polarity_activity(data);
            let series = chartData.splice(0, max_categories).map(polarityBucket => {
                let data = polarityBucket.reverse.entry_count.buckets.filter(b => b.doc_count > 0).map(b => {
                    return {
                        x: b.key,
                        y: b.doc_count
                    };
                });
                return {
                    name: polarityBucket.key,
                    data
                };
            });
            return {
                title: chartNames.polarityActivity || "Actividad Polaridad",
                type: "line-chart",
                options: {
                    plotOptions: {
                        xaxis: {
                            type: "datetime"
                        },
                        legend: {
                            show: true,
                            position: "bottom"
                        },
                    },
                    series: series
                },
            };
        },
        category_topic: data => {
            const chartData = dataSources.category_topic(data);
            let categories = chartData.map(categoryBucket => {
                let topics = categoryBucket.categories_topic.buckets.map(topicBucket => {
                    return {
                        type: "TOPIC",
                        key: topicBucket.key,
                        text: topicBucket.key,
                        count: topicBucket.doc_count
                    };
                });
                return {
                    type: "CATEGORY",
                    key: categoryBucket.key,
                    text: getCategoryDisplayName(categoryBucket.key),
                    color: getCategoryColor(categoryBucket.key, categoryColorMap),
                    count: categoryBucket.doc_count,
                    topics: _orderby(topics, (o) => -o.count)
                };
            });
            return {
                title: chartNames.topics || "Temas",
                type: "category-topic",
                options: {
                    categoryName: chartNames.topicsCategoryName || "Categorias",
                    categories
                },
            };
        },
        entities_link: (data, globalMetadata) => {
            const vizData = dataSources.entities_link(data);
            const MIN_SUPPORT = 3;
            const MIN_PROB = 0.7;
            let cooccurrences = {};
            vizData.forEach(result => {
                result._source.metadata.sentences.forEach(sentence => {
                    let entities = sentence.entities;
                    for (let k1 = 0; k1 < entities.length; k1++) {
                        let k1Key = entities[k1].key;
                        if (!(k1Key in cooccurrences)) {
                            cooccurrences[k1Key] = {};
                        }
                        for (let k2 = 0; k2 < entities.length; k2++) {
                            let k2Key = entities[k2].key;
                            if (k2Key in cooccurrences[k1Key]) {
                                cooccurrences[k1Key][k2Key] += 1;
                            } else {
                                cooccurrences[k1Key][k2Key] = 1;
                            }
                        }
                    }
                });
            });

            let globalFrequencies = globalMetadata.entities.global_frequencies;

            let nodes = [];
            let edges = [];
            let nodeKeysSet = new Set();
            let edgeKeysSet = new Set();

            Object.entries(cooccurrences).forEach(([entity1Key, entity1]) => {
                Object.entries(entity1).forEach(([entity2Key, value]) => {
                    let entity1GlobalFreq = entity1Key in globalFrequencies ? globalFrequencies[entity1Key] : 1;
                    let cooccurrenceWeighted = value / entity1GlobalFreq;
                    if (entity1Key != entity2Key && cooccurrenceWeighted >= MIN_PROB && entity1GlobalFreq >= MIN_SUPPORT) {
                        if (!nodeKeysSet.has(entity1Key)) {
                            let entity1Temp = splitIndexKey(entity1Key);
                            nodes.push({
                                id: entity1Key,
                                label: entity1Temp.text,
                                group: entity1Temp.type
                            });
                            nodeKeysSet.add(entity1Key);
                        }
                        if (!nodeKeysSet.has(entity2Key)) {
                            let entity2Temp = splitIndexKey(entity2Key);
                            nodes.push({
                                id: entity2Key,
                                label: entity2Temp.text,
                                group: entity2Temp.type
                            });
                            nodeKeysSet.add(entity2Key);
                        }
                        let keyArray = [entity1Key, entity2Key].sort()
                        let edgeKey = `${keyArray[0]}-${keyArray[1]}`;
                        if (!edgeKeysSet.has(edgeKey)) {
                            edges.push({
                                from: entity1Key,
                                to: entity2Key
                            });
                            edgeKeysSet.add(edgeKey);
                        }
                    }
                });
            });

            let plotOptions = {
                autoResize: true,
                width: "100%",
                interaction: {
                    hover: true
                },
                groups: {
                    LOC: {
                        shape: "icon",
                        icon: {
                            face: "FontAwesome",
                            size: 30,
                            code: entitiesTypeMap.LOC.faIconCode,
                            color: entitiesTypeMap.LOC.color
                        }
                    },
                    PER: {
                        shape: "icon",
                        icon: {
                            face: "FontAwesome",
                            size: 30,
                            code: entitiesTypeMap.PER.faIconCode,
                            color: entitiesTypeMap.PER.color
                        }
                    },
                    ORG: {
                        shape: "icon",
                        icon: {
                            face: "FontAwesome",
                            size: 30,
                            code: entitiesTypeMap.ORG.faIconCode,
                            color: entitiesTypeMap.ORG.color
                        }
                    },
                    MENTION: {
                        shape: "icon",
                        icon: {
                            face: "FontAwesome",
                            size: 30,
                            code: entitiesTypeMap.MENTION.faIconCode,
                            color: entitiesTypeMap.MENTION.color
                        }
                    },
                    HASHTAG: {
                        shape: "icon",
                        icon: {
                            face: "FontAwesome",
                            size: 30,
                            code: entitiesTypeMap.HASHTAG.faIconCode,
                            color: entitiesTypeMap.HASHTAG.color
                        }
                    },
                    URL: {
                        shape: "icon",
                        icon: {
                            face: "FontAwesome",
                            size: 30,
                            code: entitiesTypeMap.URL.faIconCode,
                            color: entitiesTypeMap.URL.color
                        }
                    }
                },
                edges: {
                    color: {
                        highlight: "#848484",
                        hover: "#848484",
                        inherit: "from",
                        opacity: 1.0
                    }
                },
                nodes: {
                    color: {
                        background: "white",
                        highlight: {
                            border: "#2B7CE9",
                            background: "#D2E5FF"
                        },
                        hover: {
                            border: "#2B7CE9",
                            background: "#D2E5FF"
                        }
                    },
                    font: {
                        color: "black"
                    }
                },
                physics: {
                    stabilization: {
                        iterations: 10
                    }
                },
                layout: {
                    improvedLayout: false
                }
            };

            return {
                title: chartNames.linkAnalisis || "Análisis Link",
                type: "vis-network",
                options: {
                    nodes: nodes,
                    edges: edges,
                    plotOptions
                },
            }
        },
        googleMapMarkers: data => {
            const mapData = dataSources.googleMapMarkers(data);
            if (mapData) {
                const points = mapData.filter(r => {
                    if (r._source.geo || r._source.location) {
                        return r;
                    }
                }).map(r => {
                    const coordinates = r._source.geo ? r._source.geo : r._source.location;
                    return {
                        position: { lat: parseFloat(coordinates.lat), lng: parseFloat(coordinates.lon) },
                        title: r._source.title,
                        author: r._source.author,
                        creationTime: r._source.creation_time.endsWith("00:00:00") ? r._source.creation_time.split(" ")[0] : r._source.creation_time,
                        media: r._source.media,
                        content: r._source.content,
                        source: r._source.source,
                        url: r._source.url,
                    };
                });
                return {
                    title: chartNames.eventsMap || "Mapa de Eventos",
                    type: "maps-marker",
                    options: {
                        points
                    },
                }
            }
        },
        googleMapHeat: data => {
            const mapData = dataSources.googleMapMarkers(data);
            if (mapData) {
                const points = mapData.filter(r => {
                    if (r._source.geo || r._source.location) {
                        return r;
                    }
                }).map(r => {
                    const coordinates = r._source.geo ? r._source.geo : r._source.location;
                    return {
                        position: { lat: parseFloat(coordinates.lat), lng: parseFloat(coordinates.lon) },
                        url: r._source.url,
                    };
                });
                return {
                    title: chartNames.hotMap || "Mapa de Calor",
                    type: "maps-heat",
                    options: {
                        points
                    }
                }
            }
        },
        vectorMapWorld: data => {
            const vizData = dataSources.vectorMapWorld(data);
            if (vizData) {
                const countries = vizData.map(b => {
                    return b.key;
                }).splice(0, max_categories);
                return {
                    title: chartNames.worldMap || "Mapa Mundial",
                    type: "vector-map",
                    options: {
                        map: "world",
                        places: countries,
                    }
                }
            }
        },
        vectorMapColombia: data => {
            const vizData = dataSources.vectorMapColombia(data);
            if (vizData) {
                const states = vizData.map(b => {
                    return b.key;
                }).splice(0, max_categories);
                return {
                    title: chartNames.colombiaMap || "Mapa Colombia",
                    type: "vector-map",
                    options: {
                        map: "colombia",
                        places: states,
                    },
                }
            }
        },
    };
};

export const generateDayWiseTimeSeries = function (baseval, count, yrange) {
    var i = 0;
    var series = [];
    while (i < count) {
        var x = baseval;
        var y =
            Math.floor(Math.random() * (yrange.max - yrange.min + 1)) + yrange.min;

        series.push([x, y]);
        baseval += 86400000;
        i++;
    }
    return series;
}
